import { compareByOceanStatusOrder, compareByTimeline } from './ocean-status-order.mjs?v=20260806.100';

const FINANCE_ROLES = new Set(['admin', 'finance', 'accounting']);

function dateFromMillis(value) {
  return Number.isSafeInteger(value) && value >= 0 ? new Date(value) : null;
}

function cents(value) {
  return Number.isSafeInteger(value) ? value : 0;
}

const DATE_FIELD_KEYS = new Set([
  'created_at', 'eta_at', 'lfd_at', 'pickup_appointment_at', 'transfer_at',
  'reported_empty_at', 'return_empty_lfd_at', 'actual_empty_returned_at', 'empty_returned_at',
  'special_fee_date', 'updated_at',
]);

function normalizeFieldValues(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value).map(([key, fieldValue]) => [
    key,
    DATE_FIELD_KEYS.has(key) ? dateFromMillis(fieldValue) : fieldValue,
  ]));
}

export function roleDashboardColumnPolicy(role) {
  return Object.freeze({
    showFinance: FINANCE_ROLES.has(String(role || '').toLowerCase()),
    showCustomerLink: false,
    showInternalNote: false,
  });
}

export function isCanonicalContainerComplete(container) {
  return container?.status === 'empty_returned'
    || container?.canonical_completed === true;
}

/**
 * Alan's ordering, 2026-08-08: reported empty, delivered, transferred, available,
 * inspection, pending, not arrived, returned, cancelled - and each status judged
 * by the date that governs it rather than by one shared deadline. See
 * ocean-status-order.mjs, which the Company Overview status counts read too, so
 * the table and the cards cannot disagree about what order the work is in.
 */
export function sortRoleDashboardRows(rows, { timeline = '' } = {}) {
  if (!Array.isArray(rows)) return [];
  // A timeline chosen in Filters outranks the status order: asking to see a date
  // range is asking to read the table in that date's order.
  const compare = timeline ? compareByTimeline(timeline) : compareByOceanStatusOrder;
  return [...rows].sort((left, right) => {
    // A container may only declare completion on its own status field, and that
    // outranks whatever the ocean feed last said about it.
    const leftRow = isCanonicalContainerComplete(left)
      ? { ...left, ocean_status: 'empty_returned' } : left;
    const rightRow = isCanonicalContainerComplete(right)
      ? { ...right, ocean_status: 'empty_returned' } : right;
    return compare(leftRow, rightRow);
  });
}

export function normalizeRoleDashboardSummary(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value) || value.exact !== true) {
    throw new TypeError('Dashboard summary is invalid.');
  }
  const countKeys = [
    'total', 'active', 'actionRequired', 'atRisk', 'completed7d', 'dueToday',
  ];
  const result = {};
  for (const key of countKeys) {
    if (!Number.isSafeInteger(value[key]) || value[key] < 0) {
      throw new TypeError('Dashboard summary is invalid.');
    }
    result[key] = value[key];
  }
  // The Laden / Empty / Dispatched chip counts. Optional on the way in: a client
  // running against a backend that predates them must keep working and simply show
  // no number, rather than reject the whole summary and blank the dashboard.
  // `dispatched` joined them on 2026-08-19 ("已派单要有数字") - and is not part of
  // the laden/empty partition: a released Container is loaded or empty as well, so
  // this one does not have to add up with the other two.
  for (const key of ['laden', 'empty', 'dispatched']) {
    const count = value[key];
    if (count === undefined) { result[key] = null; continue; }
    if (!Number.isSafeInteger(count) || count < 0) {
      throw new TypeError('Dashboard summary is invalid.');
    }
    result[key] = count;
  }
  // Which empties a released laden has already claimed, worked out server-side
  // across the whole fleet. The pairing picker narrows on this, so the Empty
  // chip's number stays the number of rows the picker offers. Optional for the
  // same reason the two counts are.
  const claimed = value.claimedEmptyContainerNos;
  result.claimedEmptyContainerNos = Array.isArray(claimed)
    ? claimed.filter((entry) => typeof entry === 'string' && entry.trim())
    : [];
  // What the Shipping Line and Terminal menus have learned: every value the fleet
  // carries, counted server-side over the same fleet walk as the pools above. A
  // value typed into a menu's Custom box is on a Container as soon as it saves, so
  // it is in this list on the next load, for everyone (Alan, 2026-08-21). Read
  // like the rest of this summary - an explicit key list, so an older backend that
  // does not send it leaves empty menus-plus-seed rather than breaking the table.
  // Which Containers a driver can see on their phone right now (Alan, 2026-08-21).
  // Answered by the callable from the dispatch tasks themselves, not from each
  // Container's task pointer - one released Container carries no pointer while
  // two live tasks sit on a driver's phone, and the row read "not dispatched".
  // Empty rather than null: an older backend leaves the highlight off, which is
  // the safe direction for a marker that says "do not touch this one".
  const activeDispatch = value.activeDispatchContainerIds;
  result.activeDispatchContainerIds = Array.isArray(activeDispatch)
    ? activeDispatch.filter((entry) => typeof entry === 'string' && entry.trim())
    : [];
  // The 可提 chip's count and its days (Alan, 2026-09-26). Null from a backend
  // that predates it, so the chip shows no number rather than a wrong one.
  const appointments = value.appointments;
  result.appointments = appointments && Number.isSafeInteger(appointments.total) && appointments.total >= 0
    ? {
      total: appointments.total,
      days: Object.fromEntries(Object.entries(appointments.days ?? {})
        .filter(([day, count]) => /^\d{4}-\d{2}-\d{2}$/.test(day) && Number.isSafeInteger(count) && count > 0)),
    }
    : null;
  const fieldOptions = value.fieldOptions;
  result.fieldOptions = {};
  for (const key of ['ssl', 'terminal_name']) {
    const values = fieldOptions?.[key];
    result.fieldOptions[key] = Array.isArray(values)
      ? values.filter((entry) => typeof entry === 'string' && entry.trim())
      : [];
  }
  // Status / shipping line / terminal combinations for the Filters menus (2026-10-17). Named here or this
  // whitelisting normalizer drops it, which is exactly what happened on the first try.
  result.filterFacets = Array.isArray(value.filterFacets)
    ? value.filterFacets.filter((tuple) => Array.isArray(tuple) && tuple.length === 3
      && tuple.every((entry) => typeof entry === 'string')).map((tuple) => [...tuple])
    : null;
  // How many Containers are still without ETA, shipping line and terminal
  // (Alan, 2026-08-13). This normalizer rebuilds the summary from an explicit key
  // list rather than passing the object through, so a new count that is not named
  // here is silently dropped - the server sent it, the chip read undefined, and
  // the badge rendered blank. Optional on the way in for the same reason the two
  // cargo counts are: an older backend must not blank the dashboard.
  const missing = value.missing;
  result.missing = {};
  for (const key of ['eta', 'ssl', 'terminal']) {
    const count = missing?.[key];
    if (count === undefined) { result.missing[key] = null; continue; }
    if (!Number.isSafeInteger(count) || count < 0) {
      throw new TypeError('Dashboard summary is invalid.');
    }
    result.missing[key] = count;
  }
  const revenue = value.revenue7dCents;
  if (revenue !== null && (!Number.isSafeInteger(revenue) || revenue < 0)) {
    throw new TypeError('Dashboard summary is invalid.');
  }
  return { ...result, revenue7dCents: revenue, exact: true };
}

export function normalizeRoleDashboardRow(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError('Role Dashboard row must be an object.');
  }
  const containerId = typeof value.container_id === 'string' ? value.container_id.trim() : '';
  const containerNo = typeof value.container_no === 'string' ? value.container_no.trim() : '';
  if (!containerId || !containerNo || value.schema_version !== 1) {
    throw new TypeError('Role Dashboard row identity is invalid.');
  }
  const task = value.dispatch_task && typeof value.dispatch_task === 'object'
    ? value.dispatch_task : null;
  const finance = value.finance_summary && typeof value.finance_summary === 'object'
    ? value.finance_summary : {};
  const fieldValues = normalizeFieldValues(value.field_values);
  return {
    ...fieldValues,
    id: containerId,
    information_revision: Number.isSafeInteger(value.information_revision)
      && value.information_revision >= 0 ? value.information_revision : 0,
    safe_projection: true,
    container_no: containerNo,
    status: String(value.status || 'pending'),
    current_stage: String(value.current_stage || value.status || 'pending'),
    canonical_completed: value.canonical_completed === true,
    is_empty_returned: value.canonical_completed === true,
    urgent_handle: value.urgent_handle === true,
    is_past_due: value.is_past_due === true,
    priority: String(value.priority || 'normal'),
    current_action_type: value.current_action_type || null,
    current_responsible_role: value.current_responsible_role || null,
    next_action: value.current_action_type || null,
    responsible_role: value.current_responsible_role || null,
    critical_deadline: dateFromMillis(value.due_at_millis)
      || dateFromMillis(value.lfd_at_millis)
      || dateFromMillis(value.pickup_appointment_at_millis),
    job_no: value.job_no || '',
    dc_container_no: value.dc_container_no || '',
    master_bl_no: value.master_bl_no || '',
    size_type: value.size_type || '',
    ssl: value.ssl || '',
    terminal_name: value.terminal_name || '',
    customer_name: fieldValues.customer_name || value.customer_name || '',
    assigned_driver_name: value.assigned_driver_name || task?.driver_name || '',
    created_at: fieldValues.created_at || dateFromMillis(value.created_at_millis),
    updated_at: fieldValues.updated_at || dateFromMillis(value.updated_at_millis),
    eta_at: fieldValues.eta_at || dateFromMillis(value.eta_at_millis),
    lfd_at: fieldValues.lfd_at || dateFromMillis(value.lfd_at_millis),
    due_at: dateFromMillis(value.due_at_millis),
    pickup_appointment_at: fieldValues.pickup_appointment_at
      || dateFromMillis(value.pickup_appointment_at_millis),
    delivery_appointment_at: dateFromMillis(value.delivery_appointment_at_millis),
    load_out_at: dateFromMillis(value.load_out_at_millis),
    picked_up_at: dateFromMillis(value.load_out_at_millis),
    // Release state, which the projection did not carry until now. Without it
    // the Task modal drew "not released" on released work, and the dashboard's
    // Active count - which keys off dispatch_released_at - could only be zero.
    dispatch_released: fieldValues.dispatch_released === true,
    dispatch_released_at: dateFromMillis(value.dispatch_released_at_millis),
    dispatch_released_legs: Array.isArray(fieldValues.dispatch_released_legs)
      ? fieldValues.dispatch_released_legs : [],
    loaded_delivered_at: dateFromMillis(value.loaded_delivered_at_millis),
    warehouse_arrived_at: dateFromMillis(value.loaded_delivered_at_millis),
    empty_ready_at: dateFromMillis(value.empty_ready_at_millis),
    empty_picked_up_at: dateFromMillis(value.empty_picked_up_at_millis),
    empty_returned_at: fieldValues.empty_returned_at
      || dateFromMillis(value.empty_returned_at_millis),
    dispatch_summary: task ? {
      state: 'active',
      driverName: task.driver_name || value.assigned_driver_name || null,
      taskId: task.task_id || null,
    } : {
      state: 'not_officially_dispatched',
      driverName: null,
      taskId: null,
    },
    // The server DERIVES Pier Pass from the two fee chips (functions/src/pier-pass-fees.ts) and sends it in
    // field_values; the stored finance.pier_pass_cents is a leftover that is 0 on nearly every Container. This
    // line used to overwrite the derived number with the stored one, so the column read US$0.00 beside two
    // green Paid chips (Alan, 2026-10-11). Use the derived value whenever the server sent one.
    pier_pass_cents: Number.isSafeInteger(fieldValues.pier_pass_cents)
      ? fieldValues.pier_pass_cents : cents(finance.pier_pass_cents),
    ctf_cents: cents(finance.ctf_cents),
    demurrage_cents: cents(finance.demurrage_cents),
    other_charge_cents: cents(finance.other_charge_cents),
    ctf_status: typeof finance.ctf_status === 'string' ? finance.ctf_status : null,
    tmf_status: typeof finance.tmf_status === 'string' ? finance.tmf_status : null,
    ...(Number.isSafeInteger(finance.total_revenue_cents)
      ? { total_revenue_cents: finance.total_revenue_cents }
      : {}),
    exam_fee_cents: 0,
    per_diem_cents: 0,
  };
}

export async function loadAllRoleDashboardRows(callList, options = {}) {
  if (typeof callList !== 'function') throw new TypeError('Dashboard callable is required.');
  const pageLimit = Number.isSafeInteger(options.pageLimit) ? options.pageLimit : 100;
  const maxRows = Number.isSafeInteger(options.maxRows) ? options.maxRows : 1000;
  const rows = [];
  let cursor = null;
  let hasMore = true;
  let omittedRows = false;
  while (hasMore && rows.length < maxRows) {
    const remaining = maxRows - rows.length;
    // ALWAYS a whole page. This asked for Math.min(pageLimit, remaining), which
    // computes an arbitrary number - and the callable accepts only the sizes
    // DashboardPageLimit names, so anything else is refused outright with
    // "The dashboard query is invalid." (Alan, 2026-08-13).
    //
    // It was latent for as long as the page size was 300, because 1000 happens to
    // divide into 300/300/300/100 and 100 is itself an allowed size. At 600 the
    // second page asks for 400 and there is no such page size. Overshoot was
    // never the problem: acceptedItems below already trims the page to `remaining`.
    const response = await callList({ limit: pageLimit, cursor });
    const data = response?.data;
    if (!data || !Array.isArray(data.items)) throw new TypeError('Dashboard response is invalid.');
    const pageHasMore = data.hasMore === true;
    const nextCursor = typeof data.nextCursor === 'string' && data.nextCursor.length > 0
      ? data.nextCursor : null;
    if (pageHasMore && (data.items.length === 0 || !nextCursor || nextCursor === cursor)) {
      throw new TypeError('Dashboard pagination is invalid.');
    }
    const acceptedItems = data.items.slice(0, remaining);
    omittedRows ||= acceptedItems.length < data.items.length;
    rows.push(...acceptedItems.map(normalizeRoleDashboardRow));
    hasMore = pageHasMore;
    cursor = hasMore ? nextCursor : null;
  }
  return {
    rows,
    truncated: omittedRows || hasMore,
  };
}
